/* eslint-disable @typescript-eslint/no-explicit-any */
import { safeLocalStorageClear } from '@global/helpers/general';
import authReducer from '@redux/slices/auth';
import behaviorReducer from '@redux/slices/behavior';
import formDataReducer from '@redux/slices/form-data';
import miscReducer from '@redux/slices/misc';
import profileReducer from '@redux/slices/profile';
import verificationReducer from '@redux/slices/verification';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { navigateTo } from '@services/navigation';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE
} from 'redux-persist';
import storageSession from 'redux-persist/es/storage/session';
import storage from 'redux-persist/lib/storage';

const persistConfig = {
  key: 'root-v3',
  storage,
  blacklist: ['behavior']
};

// store form data in session storage
const formDataPersistConfig = { key: 'formData', storage: storageSession };
const verificationPersistConfig = { key: 'verification', storage: storageSession };
const profilePersistConfig = { key: 'profile', storage: storageSession };
const miscPersistConfig = { key: 'misc', storage: storageSession };

const rootReducer = combineReducers({
  auth: authReducer,
  behavior: behaviorReducer,
  misc: persistReducer(miscPersistConfig, miscReducer),
  formData: persistReducer(formDataPersistConfig, formDataReducer),
  verification: persistReducer(verificationPersistConfig, verificationReducer),
  profile: persistReducer(profilePersistConfig, profileReducer)
});

const appReducer = (state: any, action: any) => {
  // on log out:- Remove the local storage and reset the store to initial state
  if (action.type === 'auth/logout') {
    safeLocalStorageClear();
    sessionStorage.clear();
    clearInterval(window.intervalId);
    window.intervalId = 0;
    navigateTo('/login');
    return rootReducer(undefined, action);
  }

  return rootReducer(state, action);
};

const persistedReducer = persistReducer(persistConfig, appReducer);

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  // Optionally pass options listed below
  stateTransformer: (state) => {
    // remove sensitive data from state
    const newState = { ...state };
    delete newState.auth;
    delete newState._persist;
    return newState;
  }
});

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
      }
    }),
  enhancers: (getDefaultEnhancers) => {
    return getDefaultEnhancers().concat(sentryReduxEnhancer);
  }
});

const persistor = persistStore(store);

export { persistor, store };

export type RootState = ReturnType<typeof rootReducer>;
